import { faArrowRight, faXmark } from '@fortawesome/free-solid-svg-icons';
import { faArrowLeft } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Animated, Easing, NativeScrollEvent, NativeSyntheticEvent, Platform, Pressable, ScrollView, StyleSheet, View } from 'react-native';
import useCustomTheme from '../../hooks/theme-hook';
import CustomCarouselPagination from './CarouselPagination';
import { useAppDispatch } from '../../hooks/store-hook';
import { lightBoxActions } from '../../store/lightBoxSlice';
import { myAccountActions } from '../../store/myAccountSlice';
import NavigationElements from '../../models/NavigationElements';

export interface CustomCarouselProps {
  children: any;
  onClose?(): void;
  bigView?: boolean;
  onPageChange?(currentPage: number): void;
  closeIconColor?: string;
  renderFooter?(pageIndex: number): React.ReactNode;
  renderFixedFooter?(currentPage: number): React.ReactNode;
  renderFooterBackground?(pageIndex: number): React.ReactNode;
  footerHeight?: number;
  controlsBottomInset?: number;
  // Keep mobile arrows inside the artwork, independent of popup/text height.
  navigationImageAspectRatio?: number;
  // Forces slide direction. 1 = right-to-left, -1 = left-to-right.
  forceDirection?: 1 | -1;
  enableSwipe?: boolean;
}

const bulletColors = ['#43a49e', '#abadf0', '#f5a523', '#1473be'];
const bulletActiveColors = ['#035164', '#5e51a3', '#8e183d', '#00324a'];
const AnimatedScrollView = Animated.createAnimatedComponent(ScrollView);
const useNativeAnimationDriver = Platform.OS !== 'web';

type CarouselDirection = 1 | -1;
type CarouselTransition =
  | {
      from: number;
      to: number;
      direction: CarouselDirection;
    }
  | undefined;

const CustomCarousel = (props: CustomCarouselProps) => {
  const theme = useCustomTheme();
  const dispatch = useAppDispatch();
  const style = useCarouselStyles();

  const pages = useMemo(() => React.Children.toArray(props.children), [props.children]);
  const numberOfPages = pages.length;

  const [currentPage, setCurrentPage] = useState<number>(0);
  const [transition, setTransition] = useState<CarouselTransition>(undefined);
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const [showCloseButton, setShowCloseButton] = useState<boolean>(props.onClose ? true : false);
  const [isVisible, setIsVisible] = useState(true);

  const pagingRef = useRef<ScrollView>(null);
  const isAnimating = !!transition;
  const animProgress = useRef(new Animated.Value(0)).current;
  const scrollX = useRef(new Animated.Value(0)).current;
  const isDesktopWebBigView = props.bigView && Platform.OS === 'web';
  const useSwipePaging = !!props.enableSwipe && numberOfPages > 1;
  const hasFixedFooter = !!props.renderFixedFooter && !!props.renderFooterBackground && (props.footerHeight ?? 0) > 0;
  const controlsBottomInset = props.controlsBottomInset ?? 0;
  const desktopWebBigViewArrowStyle = isDesktopWebBigView
    ? ({
        bottom: -44,
        width: 45,
        height: 45,
        borderRadius: 23,
      } as const)
    : undefined;
  const arrowIconSize = isDesktopWebBigView ? 21 : 25;
  const imageArrowStyle = !props.bigView && props.navigationImageAspectRatio && containerWidth > 0
    ? { top: Math.max(0, containerWidth / props.navigationImageAspectRatio - 56 - 8), transform: [{ translateY: 0 }] }
    : undefined;

  const getBulletActiveColor = (pageIndex: number) =>
    (bulletActiveColors[pageIndex] ?? bulletActiveColors[bulletActiveColors.length - 1] ?? theme.text.primary)?.toString();

  const startTransition = React.useCallback((to: number) => {
    if (isAnimating) return;
    if (to < 0 || to >= numberOfPages) return;
    if (to === currentPage) return;

    if (useSwipePaging) {
      setCurrentPage(to);
      props.onPageChange && props.onPageChange(to);

      if (containerWidth > 0) {
        pagingRef.current?.scrollTo({ x: containerWidth * to, animated: true });
      }
      return;
    }

    const from = currentPage;
    const direction: CarouselDirection = props.forceDirection ?? (to > from ? 1 : -1);

    // Update UI state immediately (dots, arrow tint, background-change callback).
    setCurrentPage(to);
    props.onPageChange && props.onPageChange(to);

    // Without a measured width yet, fall back to instant switching.
    if (containerWidth <= 0) {
      return;
    }

    setTransition({ from, to, direction });
    animProgress.setValue(0);
    Animated.timing(animProgress, {
      toValue: 1,
      duration: 280,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: useNativeAnimationDriver,
    }).start(({ finished }) => {
      if (!finished) return;
      setTransition(undefined);
    });
  }, [animProgress, containerWidth, currentPage, isAnimating, numberOfPages, props.forceDirection, props.onPageChange, useSwipePaging]);

  const handleOnPagePress = (page: number) => {
    startTransition(page);
  };

  const handleOnPreviousPress = () => {
    startTransition(currentPage - 1);
  };

  const handleOnNextPress = () => {
    startTransition(currentPage + 1);
  };

  const handleNavigateToLink = (page: string) => {
    dispatch(lightBoxActions.setVisibility(false));
    dispatch(myAccountActions.setVisibility(true));
    if (page === 'myRewards') {
      dispatch(myAccountActions.navigate(NavigationElements.myRewards));
    } else if (page === 'myProfil') {
      dispatch(myAccountActions.navigate(NavigationElements.myProfile));
    }
  };

  const handleOnClosePress = () => {
    props.onClose && props.onClose();
  };

  useEffect(() => {
    if (!isVisible) {
      handleOnClosePress();
    }
  }, [isVisible]);

  useEffect(() => {
    if (!useSwipePaging || containerWidth <= 0) {
      return;
    }

    pagingRef.current?.scrollTo({ x: containerWidth * currentPage, animated: false });
    scrollX.setValue(containerWidth * currentPage);
  }, [containerWidth, scrollX, useSwipePaging]);

  const renderPageContent = (index: number) => {
    const child = pages[index];
    if (!child) return null;

    const pageContent = React.isValidElement(child) ? React.cloneElement(child as any, { onNavigateToLink: handleNavigateToLink }) : child;

    return (
      <View style={style.pageContainer}>
        <View style={style.pageBody}>{pageContent}</View>
        {!hasFixedFooter && props.renderFooter && props.renderFooter(index)}
      </View>
    );
  };

  const renderPage = (index: number) => {
    const pageContent = renderPageContent(index);
    if (!pageContent) return null;

    return (
      <View key={`carousel_page_${index}`} style={style.pageViewport}>
        {pageContent}
      </View>
    );
  };

  const renderSwipePage = (index: number) => {
    // A horizontal ScrollView has no bounded content width before layout.
    // Wait for that measurement so artwork cannot render at its intrinsic size.
    if (containerWidth <= 0) return null;

    const pageContent = renderPageContent(index);
    if (!pageContent) return null;

    return (
      <View key={`carousel_swipe_page_${index}`} style={[style.swipePage, { width: containerWidth }]}>
        {pageContent}
      </View>
    );
  };

  const renderAnimatedPages = () => {
    if (!transition || containerWidth <= 0) {
      return renderPage(currentPage);
    }

    const { from, to, direction } = transition;

    // iOS can show 1-frame "tearing"/flicker on large images inside an animated,
    // clipped container when opacity is animated. Rasterize the animated layer
    // and avoid opacity crossfades to keep the transition stable.
    const rasterizeStyle = {
      backfaceVisibility: 'hidden' as const,
      needsOffscreenAlphaCompositing: true,
      shouldRasterizeIOS: true,
      renderToHardwareTextureAndroid: true,
    };

    const fromTranslateX = animProgress.interpolate({
      inputRange: [0, 1],
      outputRange: [0, -direction * containerWidth],
    });

    const toTranslateX = animProgress.interpolate({
      inputRange: [0, 1],
      outputRange: [direction * containerWidth, 0],
    });

    return (
      <>
        <Animated.View style={[style.animatedPage, rasterizeStyle, { transform: [{ translateX: fromTranslateX }] }]}>
          {renderPage(from)}
        </Animated.View>
        <Animated.View style={[style.animatedPage, rasterizeStyle, { transform: [{ translateX: toTranslateX }] }]}>
          {renderPage(to)}
        </Animated.View>
      </>
    );
  };

  const handleSwipeMomentumEnd = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    if (!containerWidth) {
      return;
    }

    const x = event.nativeEvent.contentOffset.x ?? 0;
    const page = Math.max(0, Math.min(numberOfPages - 1, Math.round(x / containerWidth)));

    if (page === currentPage) {
      return;
    }

    setCurrentPage(page);
    props.onPageChange && props.onPageChange(page);
  };

  const renderFooterBackgroundPage = (index: number) => {
    if (!props.renderFooterBackground) {
      return null;
    }

    return (
      <View key={`carousel_footer_background_${index}`} style={style.footerBackgroundPage}>
        {props.renderFooterBackground(index)}
      </View>
    );
  };

  const renderFixedFooterBackground = () => {
    if (!hasFixedFooter) {
      return null;
    }

    if (useSwipePaging) {
      if (containerWidth <= 0) {
        return renderFooterBackgroundPage(currentPage);
      }

      return (
        <Animated.View
          style={[
            style.footerBackgroundTrack,
            {
              width: containerWidth * numberOfPages,
              transform: [{ translateX: Animated.multiply(scrollX, -1) }],
            },
          ]}
        >
          {pages.map((_, index) => (
            <View key={`carousel_footer_swipe_background_${index}`} style={[style.footerBackgroundSwipePage, { width: containerWidth }]}>
              {props.renderFooterBackground?.(index)}
            </View>
          ))}
        </Animated.View>
      );
    }

    if (!transition || containerWidth <= 0) {
      return renderFooterBackgroundPage(currentPage);
    }

    const { from, to, direction } = transition;
    const fromTranslateX = animProgress.interpolate({
      inputRange: [0, 1],
      outputRange: [0, -direction * containerWidth],
    });
    const toTranslateX = animProgress.interpolate({
      inputRange: [0, 1],
      outputRange: [direction * containerWidth, 0],
    });

    return (
      <>
        <Animated.View style={[style.footerAnimatedBackground, { transform: [{ translateX: fromTranslateX }] }]}>
          {renderFooterBackgroundPage(from)}
        </Animated.View>
        <Animated.View style={[style.footerAnimatedBackground, { transform: [{ translateX: toTranslateX }] }]}>
          {renderFooterBackgroundPage(to)}
        </Animated.View>
      </>
    );
  };

  return (
    <View
      style={style.container}
      onLayout={(e) => {
        setContainerWidth(e.nativeEvent.layout.width);
      }}
    >
      {useSwipePaging ? (
        <AnimatedScrollView
          ref={pagingRef}
          style={style.pagesContainer}
          contentContainerStyle={[style.swipePagesContent, { width: containerWidth > 0 ? containerWidth * numberOfPages : undefined }]}
          horizontal
          pagingEnabled
          nestedScrollEnabled
          directionalLockEnabled
          bounces={false}
          overScrollMode="never"
          showsHorizontalScrollIndicator={false}
          onScroll={Animated.event([{ nativeEvent: { contentOffset: { x: scrollX } } }], { useNativeDriver: useNativeAnimationDriver })}
          onMomentumScrollEnd={handleSwipeMomentumEnd}
          scrollEventThrottle={16}
        >
          {pages.map((_, index) => renderSwipePage(index))}
        </AnimatedScrollView>
      ) : (
        <View style={style.pagesContainer}>{renderAnimatedPages()}</View>
      )}
      {hasFixedFooter && (
        <View style={[style.fixedFooterContainer, { height: props.footerHeight }]}>
          <View style={style.fixedFooterBackgroundViewport}>{renderFixedFooterBackground()}</View>
          <View style={style.fixedFooterOverlay}>{props.renderFixedFooter?.(currentPage)}</View>
        </View>
      )}
      {showCloseButton && (
        <Pressable style={style.closeButton} onPress={handleOnClosePress} disabled={isAnimating}>
          <FontAwesomeIcon
            style={style.closeButton.xMark}
            icon={faXmark}
            size={30}
            color={(props.closeIconColor ?? theme.background.primary)?.toString()}
          />
        </Pressable>
      )}
      <View pointerEvents="box-none" style={[style.controlsLayer, { bottom: controlsBottomInset }]}>
        {currentPage !== 0 && (
          <Pressable
            style={[props.bigView ? style.previousArrowButtonDesktop : style.previousArrowButton, desktopWebBigViewArrowStyle, imageArrowStyle]}
            onPress={handleOnPreviousPress}
            disabled={isAnimating}
          >
            <FontAwesomeIcon style={style.previousArrowButton.arrow} icon={faArrowLeft} size={arrowIconSize} color={getBulletActiveColor(currentPage)} />
          </Pressable>
        )}
        {currentPage < numberOfPages - 1 && (
          <Pressable
            style={[props.bigView ? style.nextButtonDesktop : style.nextButton, desktopWebBigViewArrowStyle, imageArrowStyle]}
            onPress={handleOnNextPress}
            disabled={isAnimating}
          >
            <FontAwesomeIcon style={style.nextButton.arrow} icon={faArrowRight} size={arrowIconSize} color={getBulletActiveColor(currentPage)} />
          </Pressable>
        )}
        {numberOfPages > 1 && (
          <CustomCarouselPagination
            bulletActiveColors={bulletActiveColors}
            bulletColors={bulletColors}
            numberOfPages={numberOfPages}
            currentPage={currentPage}
            onPagePress={handleOnPagePress}
          />
        )}
      </View>
    </View>
  );
};

const useCarouselStyles = () => {
  const theme = useCustomTheme();

  let carousel = {
    pageViewport: {
      flexGrow: 1,
      flex: 1,
    },
    pageContainer: {
      flexGrow: 1,
      flex: 1,
    },
    pageBody: {
      flexGrow: 1,
      flex: 1,
      flexShrink: 1,
    },
    pagesContainer: {
      flexGrow: 1,
      flex: 1,
      position: 'relative' as any,
      overflow: 'hidden' as any,
    },
    animatedPage: {
      ...StyleSheet.absoluteFillObject,
    },
    swipePage: {
      flexShrink: 0,
      height: '100%' as any,
    },
    swipePagesContent: {
      flexDirection: 'row' as any,
      height: '100%' as any,
    },
    container: { position: 'relative' as any, flexGrow: 1, backgroundColor: theme.background.modal },
    footerBackgroundPage: {
      flex: 1,
    },
    footerBackgroundTrack: {
      flexDirection: 'row' as any,
      height: '100%' as any,
    },
    footerBackgroundSwipePage: {
      height: '100%' as any,
      flexShrink: 0,
    },
    footerAnimatedBackground: {
      ...StyleSheet.absoluteFillObject,
    },
    fixedFooterContainer: {
      position: 'relative' as any,
      overflow: 'hidden' as any,
      flexShrink: 0,
    },
    fixedFooterBackgroundViewport: {
      ...StyleSheet.absoluteFillObject,
      overflow: 'hidden' as any,
    },
    fixedFooterOverlay: {
      ...StyleSheet.absoluteFillObject,
    },
    controlsLayer: {
      ...StyleSheet.absoluteFillObject,
    },
    closeButton: {
      position: 'absolute' as any,
      top: 20,
      right: 20,
      xMark: {
        outline: 'none' as any,
      } as any,
    },
    previousButton: {
      position: 'absolute' as any,
      left: 12,
      bottom: 10,
      label: {
        fontSize: 14,
        color: theme.text.darkTurquoiseTwo,
        textDecorationLine: 'underline' as any,
      },
    },
    previousArrowButton: {
      display: 'flex' as any,
      justifyContent: 'center' as any,
      position: 'absolute' as any,
      left: 24,
      top: '40%' as any,
      transform: [{ translateY: -50 }],
      width: 56,
      height: 56,
      borderRadius: 28,
      backgroundColor: 'white' as any,
      alignItems: 'center' as any,
      elevation: 4,
      shadowColor: 'rgba(0, 0, 0, 0.16)' as any,
      shadowOffset: { width: 0, height: 3 },
      shadowOpacity: 1,
      shadowRadius: 6,
      arrow: {
        margin: 'auto' as any,
        outline: 'none' as any,
      },
    },
    nextButton: {
      display: 'flex' as any,
      justifyContent: 'center' as any,
      position: 'absolute' as any,
      right: 24,
      top: '40%' as any,
      transform: [{ translateY: -50 }],
      width: 56,
      height: 56,
      borderRadius: 28,
      backgroundColor: 'white' as any,
      alignItems: 'center' as any,
      elevation: 4,
      shadowColor: 'rgba(0, 0, 0, 0.16)' as any,
      shadowOffset: { width: 0, height: 3 },
      shadowOpacity: 1,
      shadowRadius: 6,
      arrow: {
        margin: 'auto' as any,
        outline: 'none' as any,
      },
    },
    previousButtonDesktop: {
      position: 'absolute' as any,
      left: 34,
      bottom: 10,
    },
    previousArrowButtonDesktop: {
      position: 'absolute' as any,
      left: 38,
      bottom: 0,
      transform: [{ translateY: -50 }],
      width: 56,
      height: 56,
      borderRadius: 30,
      backgroundColor: 'white' as any,
      alignItems: 'center' as any,
      elevation: 4,
      shadowColor: 'rgba(0, 0, 0, 0.16)' as any,
      shadowOffset: { width: 0, height: 3 },
      shadowOpacity: 1,
      shadowRadius: 6,
      arrow: {
        margin: 'auto' as any,
        outline: 'none' as any,
      },
    },
    nextButtonDesktop: {
      position: 'absolute' as any,
      right: 38,
      bottom: 0,
      transform: [{ translateY: -50 }],
      width: 56,
      height: 56,
      borderRadius: 30,
      backgroundColor: 'white' as any,
      alignItems: 'center' as any,
      elevation: 4,
      shadowColor: 'rgba(0, 0, 0, 0.16)' as any,
      shadowOffset: { width: 0, height: 3 },
      shadowOpacity: 1,
      shadowRadius: 6,
      arrow: {
        margin: 'auto' as any,
        outline: 'none' as any,
      },
    },
  };

  return carousel as typeof carousel;
};

export default CustomCarousel;
